import {
    Button, ClickEvent, DataSource, DataSourceMode, Image, ImageType, Layout, LookupModelSelectionEvent, SlideoutDecorator, Textbox,
    ValidationResult
} from "@mcleod/components";
import { EmailUtil, Model, ModelRow } from "@mcleod/core";
import { getDispatchControlValue, LMEUserSettings, ModelReportTemplate } from "@mcleod/general";
import { AutogenLayoutQuoteSendEmail } from "./autogen/AutogenLayoutQuoteSendEmail";

export class QuoteSendEmail extends AutogenLayoutQuoteSendEmail {
    private doOnSlideoutClosed: (cancelled: boolean) => void;

    public static show(props: QuoteSendEmailProps) {
        const layout = Layout.getLayout("lme/powerbroker/QuoteSendEmail") as QuoteSendEmail;
        layout.addLayoutLoadListener(async () => {
            const rowEmail = new ModelRow("lme/dispatch/send-quote-email", false);
            layout.doOnSlideoutClosed = props.doOnSlideoutClosed;
            const rowQuote = props.sourceQuote.data[0];
            const sourceStop = props.sourceQuote.childDataSources.find(ds => ds.id === "sourceQuoteStop");
            const shipperStop = sourceStop.data[0];
            const consigneeStop = sourceStop.data[sourceStop.data.length - 1];

            const quoteID = rowQuote.get("id", "");
            const fromCityState = `${shipperStop.get("city_name")}, ${shipperStop.get("state")}`;
            const toCityState = `${consigneeStop.get("city_name")}, ${consigneeStop.get("state")}`;

            // set default values
            rowEmail.set("to_address", rowQuote.get("customer_email", null));
            rowEmail.set("subject", `Quote ${quoteID} for ${fromCityState} to ${toCityState}`);
            const contactName = rowQuote.get("customer_contact_name", null)
            rowEmail.setLookupModelData("contact_name", rowQuote.getFirstLookupModelData("customer_contact_name"));
            rowEmail.set("contact_name", contactName);
            rowEmail.set("order_id", quoteID);
            rowEmail.set("is_ltl", props.isLtl);

            const userSettings = LMEUserSettings.get();
            const templateNum = props.isLtl ? userSettings.ltl_quote_template ?? getDispatchControlValue("brk_quote_template") : userSettings.quote_template;
            if (templateNum != null) {
                await new ModelReportTemplate().searchSingle({ template_number: templateNum, document_type: rowEmail.get("is_ltl") ? "Y" : "Q" }).then(tempRow => {
                    rowEmail.setLookupModelData("template_num", tempRow)
                    layout.mainDataSource.displayDataInBoundComponents();
                });
                rowEmail.set("template_num", templateNum);
            }

            layout.mainDataSource.addRow(rowEmail, 0, true);
            layout.mainDataSource.rowIndex = 0;
            layout.loadPreview(rowEmail);

            layout.textboxTemplateNum.addLookupModelSelectionListener(() => layout.loadPreview(rowEmail));
            layout.textboxToAddress.addlValidationCallback = (value: string, mode: DataSourceMode): ValidationResult => {
                return layout.validateEmails(value, mode);
            };
            layout.mainDataSource.addAfterExecutionListener(() => layout.closeOverlay(false));

            // show the slideout
            new SlideoutDecorator({
                layout: layout,
                width: 1200,
                zIndex: 5,
                fillVerticalSpace: true,
                backgroundColor: "defaultBackground",
                title: "Send Email",
                addlComponents: [layout.getSendButton()],
                onClose: (cancelled: boolean) => layout.closeOverlay(cancelled),
                ...props
            });
        });
    }

    private loadPreview(rowEmail: ModelRow) {
        if (this.textboxTemplateNum.validateSimple()) {
            const image = new Image({ imageType: ImageType.PDF, fillHeight: true, fillRow: true, id: "quoteSheetPreview" });
            Model.searchSingleRecord("lme/dispatch/send-quote-email", rowEmail).then(row => {
                image.imageBytes = row.get("document");
                this.panelPreview.removeAll();
                this.panelPreview.add(image);
            });
        }
    }

    private getSendButton(): Button {
        return new Button({
            caption: "Send",
            width: 150,
            paddingLeft: 20,
            paddingRight: 20,
            rowBreak: false,
            color: "default.reverse",
            backgroundColor: "primary",
            borderWidth: 0,
            busyWhenDataSourceBusy: true,
            dataSource: this.mainDataSource,
            onClick: async (event: ClickEvent) => {
                this.mainDataSource.post();
            }
        });
    }

    public async closeOverlay(cancelled: boolean) {
        this.slideOut().then(() => {
            if (this.doOnSlideoutClosed != null)
                this.doOnSlideoutClosed(cancelled);
        });
    }

    textboxTemplateNumBeforeLookupModelSearch(event) {
        event.filter.document_type = this.activeRow.get("is_ltl") ? "Y" : "Q";
        event.filter.template_number = ">0";
    }

    textboxContactNameBeforeLookupModelSearch(event) {
        if (this.activeRow.isNull("customer_id")) {
            event.preventDefault();
        } else {
            event.filter.parent_row_id = this.activeRow.get("customer_id");
            event.filter.parent_row_type = "C";
            event.filter.is_active = "Y";
        }
        const contactName = event.target as Textbox;
        if (contactName != null)
            contactName.addLookupModelSelectionListener(this.textboxContactNameOnLookupModelSelection);
    }

    private textboxContactNameOnLookupModelSelection = (event: LookupModelSelectionEvent) => {
        const selectedRow = event.selection;
        if (selectedRow != null) {
            this.activeRow.set("email", selectedRow.get("to_address"));
            this.mainDataSource.displayDataInBoundComponents();
        }
    }

    private validateEmails(value: string, mode: DataSourceMode): ValidationResult {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        const emails = value.split(/,/).filter(e => e.length > 0);
        const valid = emails.every(e => EmailUtil.isValidEmail(e));
        if (!valid) {
            return {
                component: this.textboxToAddress,
                isValid: false,
                validationMessage: "Please enter valid email addresses separated by commas."
            };
        }
        return null;
    }
}

export interface QuoteSendEmailProps {
    sourceQuote: DataSource;
    doOnSlideoutClosed?: (cancelled: boolean) => void;
    isLtl?: boolean;
}
